import DetailsState from "../detailsState";
import {State} from "../state";
import {compActions, CompanyActions, companyActions, CompanyLeadDependencies, IGetData} from "./companyState.types";
import type {BookingflowStateMachine} from "../../bookingflowstatemachine";
import type {DetailsStateDependencies} from "../detailsState/detailsState.types";
import { Goal, Service, ICompanyLeadSettings, Address, BasisType, CompanyLead } from "@raskrask-dk/raskrask-utils";

const COMPANY_STATE = "COMPANY";
/**
 * This state is a state where you can either choose a servicetype and go to the
 * calendar state and book a "OneTime" company booking, or become a lead
 * and sign up for being contacted by us (create a podio task).
 */
export class CompanyLeadState extends State<CompanyActions, typeof COMPANY_STATE> {
  public readonly type = COMPANY_STATE;
  private _goal: Goal | undefined;
  private _serviceType: Service | undefined;
  private _companySettings: ICompanyLeadSettings | undefined;
  private _address: Address;
  private _basisType: BasisType;

  constructor(bookingflowStateMachine: BookingflowStateMachine, dependencies: CompanyLeadDependencies) {
    super(bookingflowStateMachine);
    const {address, basisType} = dependencies;
    this._address = address;
    this._basisType = basisType;
  }

  /**
   *
   * @returns - either the calendarstate, because that it is a oneTime
   * or it creates a lead in podio for a long term deal
   */
  private nextState(): DetailsState {
    const companyLead = new CompanyLead(this._companySettings as ICompanyLeadSettings, this._address);
    const detailStateDeps: DetailsStateDependencies = {
      product: companyLead,
      address: this._address,
      customerType: "Company",
      klippeCardCode: undefined,
    };
    return new DetailsState(this.bookingflowStateMachine, detailStateDeps);
  }

  doAction(action: CompanyActions): DetailsState | CompanyLeadState {
    switch (action.type) {
      case "SET_GOAL":
        this._goal = action.data;
        return this;
      case "SET_SERVICETYPE":
        this._serviceType = action.data;
        return this;
      case "SET_COMPANY_SETTINGS":
        this._companySettings = action.data;
        return this;
      case "NEXT_STATE":
        return this.nextState();
      default:
        throw new Error("These droids are not the ones you're looking for.");
    }
  }

  public unset = (action: CompanyActions): void => {
    switch (action.type) {
      case "SET_GOAL":
        this._serviceType = undefined;
        this._companySettings = undefined;
        break;
      case "SET_SERVICETYPE":
        this._companySettings = undefined;
        break;
    }
  };

  public getData(): IGetData {
    return {
      basisType: this._basisType,
      goal: this.goal,
      serviceType: this.serviceType,
      companySettings: this.companySettings,
      product: new CompanyLead(this.companySettings as ICompanyLeadSettings, this._address),
    };
  }

  public getActions = (): compActions[] => companyActions.filter((action) => new action(this).isDoable());

  public get serviceType(): Service | undefined {
    return this._serviceType;
  }

  public get goal(): Goal | undefined {
    return this._goal;
  }

  public get companySettings(): ICompanyLeadSettings | undefined {
    return this._companySettings;
  }
}
